import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../../../shared/api/client';

/* ───────────────────────────────────────────────────────────────────────────
   Contrato — CRUD /fiscal/variables (FiscalConfigController)

   Espelha `TaxVariableResponseDto` e os DTOs de create/update
   (presentation/http/dto/fiscal-config.dto.ts).

   Tipos de variável: NUMERO | TEXTO | BOOLEANO | DATA | MONTANTE | ALIQUOTA
   Escopos: GLOBAL | TENANT | COMPANY | DOCUMENTO | ITEM
   Fontes: SISTEMA | USUARIO | CALCULADA (padrão USUARIO)

   Envelope paginado {data, total, page, limit, totalPages} nas listagens.
   O tenant vem da sessão.
   ─────────────────────────────────────────────────────────────────────────── */

export type TaxVariableType = 'NUMERO' | 'TEXTO' | 'BOOLEANO' | 'DATA' | 'MONTANTE' | 'ALIQUOTA';
export type TaxVariableScope = 'GLOBAL' | 'TENANT' | 'COMPANY' | 'DOCUMENTO' | 'ITEM';
export type TaxVariableSource = 'SISTEMA' | 'USUARIO' | 'CALCULADA';

export interface TaxVariable {
  id: string;
  tenantId: string;
  name: string;
  description: string | null;
  type: string;
  scope: string;
  source: string;
  expression: string | null;
  defaultValue: number | null;
  readOnly: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface TaxVariableInput {
  name: string;
  description?: string;
  type: TaxVariableType;
  scope: TaxVariableScope;
  source?: TaxVariableSource;
  expression?: string;
  defaultValue?: number;
  readOnly?: boolean;
}

export interface PaginatedVariables {
  data: TaxVariable[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

const VARIABLES_KEY = ['fiscal', 'variables'] as const;

export function useTaxVariables(params?: { page?: number; limit?: number }) {
  return useQuery({
    queryKey: [VARIABLES_KEY, params] as const,
    queryFn: () =>
      apiClient.get<PaginatedVariables>('/fiscal/variables', {
        params: {
          page: String(params?.page ?? 1),
          limit: String(params?.limit ?? 50),
        },
      }),
  });
}

export function useCreateTaxVariable() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: TaxVariableInput) =>
      apiClient.post<TaxVariable>('/fiscal/variables', input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: VARIABLES_KEY });
    },
  });
}

export function useUpdateTaxVariable() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...input }: TaxVariableInput & { id: string }) =>
      apiClient.put<TaxVariable>(`/fiscal/variables/${id}`, input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: VARIABLES_KEY });
    },
  });
}

export function useDeleteTaxVariable() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => apiClient.delete(`/fiscal/variables/${id}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: VARIABLES_KEY });
    },
  });
}